import type { RedirectLoginOptions } from "@auth0/auth0-spa-js";

import { createAuth0Client } from "@auth0/auth0-spa-js";
import { redirect } from "react-router";

import { clearActiveCompanyBranchId } from "~/pld/hooks/use-active-company-branch-id";
import { shutdownIntercomMessenger } from "~/pld/lib/intercom";
import { resetPostHogUser } from "~/pld/lib/posthog";
import { getSafeRedirectUrl } from "~/pld/lib/security";

let auth0ClientPromise: ReturnType<typeof createAuth0Client> | undefined;

export function getAuth0Client() {
  if (auth0ClientPromise === undefined) {
    auth0ClientPromise = createAuth0Client({
      domain: import.meta.env.VITE_AUTH0_DOMAIN,
      clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
      authorizationParams: {
        redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
      // With the in-memory cache, every reload re-runs the silent authentication
      // through a hidden iframe, and the VS Code debugger can leave that iframe
      // paused for 60 s (microsoft/vscode-js-debug#2425). The dev server keeps
      // the tokens in localStorage so a reload needs no iframe at all.
      cacheLocation: import.meta.env.DEV ? "localstorage" : "memory",
    });
  }

  return auth0ClientPromise;
}

const signoutCleanup = () => {
  clearActiveCompanyBranchId();
  resetPostHogUser();
};

export const auth0AuthProvider = {
  async isAuthenticated() {
    const client = await getAuth0Client();
    return client.isAuthenticated();
  },

  async getTokenSilently() {
    const client = await getAuth0Client();
    return client.getTokenSilently();
  },

  async isAuthenticatedOrSignIn(request?: Request) {
    const isAuthenticated = await this.isAuthenticated();

    if (!isAuthenticated) {
      // ! We can’t do this.signin here
      // ! Otherwise it cancel the dynamic loading of module and trigger a page refrseh on firefox
      // ! Hence refreshing the same page until all the route modules are loaded
      // ! This behavior was observed on dev and staging environment (and when using pnpm start)
      const returnTo = request
        ? getSafeRedirectUrl(
            new URL(request.url).pathname + new URL(request.url).search,
          )
        : undefined;
      const loginRedirectUrl = returnTo
        ? `/login-redirect?returnTo=${encodeURIComponent(returnTo)}`
        : "/login-redirect";

      throw redirect(loginRedirectUrl);
    }
  },

  async signin(type: "redirect" | "popup", options?: RedirectLoginOptions) {
    const client = await getAuth0Client();

    if (type === "redirect") {
      await client.loginWithRedirect(options);
    } else {
      await client.loginWithPopup();
    }
  },

  async handleSigninRedirect() {
    const query = window.location.search;

    if (query.includes("code=") && query.includes("state=")) {
      const client = await getAuth0Client();

      try {
        return await client.handleRedirectCallback();
      } catch {
        // We might want to log something here or discrimate against certain
        // types of errors.

        throw redirect("/", { status: 302 });
      }
    }

    return undefined;
  },

  async signout() {
    shutdownIntercomMessenger();
    const client = await getAuth0Client();
    signoutCleanup();
    await client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  },
};
